// @flow

type BuyerStatus = "FIRST_TIME_BUYER" | "MOVING_HOME" | "SECOND_HOME";

export type Input = {|
  price: number,
  buyerStatus: BuyerStatus,
|};

export type Results = {|
  ...Input,
  previousSdlt: number,
  sdltWithHoliday: number,
  saving: number,
|};

export const calculateResults = (input: Input): Results => {
  const { price, buyerStatus } = input;
  const previous = calculatePreviousSDLT(price, buyerStatus);
  const holiday = calculateCurrentSDLT(price, buyerStatus);
  return {
    ...input,
    previousSdlt: previous,
    sdltWithHoliday: holiday,
    saving: previous - holiday,
  };
};
const calculatePreviousSDLT = (price, buyerStatus) => {
  if (buyerStatus === "FIRST_TIME_BUYER" && price <= 500000) {
    return firstTimeBuyerOldSDLT(price);
  }
  return normalOldSDLT(price, buyerStatus);
};

// Source: https://www.gov.uk/government/publications/rates-and-allowances-stamp-duty-land-tax/stamp-duty-land-tax-rates-1-october-2021-to-22-september-2022--2
const PREV_TAX_FREE_THRESHOLD = 125_000;
const PREV_TWO_PERCENT_THRESHOLD = 250_000;
const PREV_FIVE_PERCENT_THRESHOLD = 925_000;
const PREV_TEN_PERCENT_THRESHOLD = 1_500_000;
const PREV_FIRST_TIME_BUYER_TAX_FREE = 300_000;

// Original SDLT
const FULL_TWO_PERCENT =
  (PREV_TWO_PERCENT_THRESHOLD - PREV_TAX_FREE_THRESHOLD) * 0.02;
const FULL_FIVE_PERCENT =
  (PREV_FIVE_PERCENT_THRESHOLD - PREV_TWO_PERCENT_THRESHOLD) * 0.05;
const FULL_TEN_PERCENT =
  (PREV_TEN_PERCENT_THRESHOLD - PREV_FIVE_PERCENT_THRESHOLD) * 0.1;
// Original first time buyer SDLT
const FTB_FULL_FIVE_PERCENT =
  (PREV_FIVE_PERCENT_THRESHOLD - PREV_FIRST_TIME_BUYER_TAX_FREE) * 0.05;

const firstTimeBuyerOldSDLT = (price) => {
  if (price < PREV_FIRST_TIME_BUYER_TAX_FREE) {
    return 0;
  }
  if (price < PREV_FIVE_PERCENT_THRESHOLD) {
    return (price - PREV_FIRST_TIME_BUYER_TAX_FREE) * 0.05;
  }
  if (price < PREV_TEN_PERCENT_THRESHOLD) {
    const ten_percent = (price - PREV_FIVE_PERCENT_THRESHOLD) * 0.1;
    return FTB_FULL_FIVE_PERCENT + ten_percent;
  }
  const twelve_percent = (price - PREV_TEN_PERCENT_THRESHOLD) * 0.12;
  return FTB_FULL_FIVE_PERCENT + FULL_TEN_PERCENT + twelve_percent;
};

const normalOldSDLT = (price, buyerStatus) => {
  const surcharge = buyerStatus === "SECOND_HOME" ? price * 0.03 : 0;
  if (price < PREV_TAX_FREE_THRESHOLD) {
    return surcharge + 0;
  }
  if (price < PREV_TWO_PERCENT_THRESHOLD) {
    return surcharge + (price - PREV_TAX_FREE_THRESHOLD) * 0.02;
  }
  if (price < PREV_FIVE_PERCENT_THRESHOLD) {
    return (
      surcharge + FULL_TWO_PERCENT + (price - PREV_TWO_PERCENT_THRESHOLD) * 0.05
    );
  }
  if (price < PREV_TEN_PERCENT_THRESHOLD) {
    return (
      surcharge +
      FULL_TWO_PERCENT +
      FULL_FIVE_PERCENT +
      (price - PREV_FIVE_PERCENT_THRESHOLD) * 0.1
    );
  }
  const twelve_percent = (price - PREV_TEN_PERCENT_THRESHOLD) * 0.12;

  return (
    surcharge +
    FULL_TWO_PERCENT +
    FULL_FIVE_PERCENT +
    FULL_TEN_PERCENT +
    twelve_percent
  );
};

const calculateCurrentSDLT = (price: number, buyerStatus: BuyerStatus) => {
  switch (buyerStatus) {
    case "FIRST_TIME_BUYER":
      return calculateFirstTimeBuyerCurrentSDLT(price);
    case "MOVING_HOME":
      return calculateSecondTimeBuyerCurrentSDLT(price);
    case "SECOND_HOME":
      // you usually pay 3% on top of these rates if you already own more than one residential property.
      return price * 0.03 + calculateSecondTimeBuyerCurrentSDLT(price);
    default:
      throw new Error("Undefined buyerStatus");
  }
};

const calculateFirstTimeBuyerCurrentSDLT = (price: number) => {
  /*
  Calculates the tax relief if the property you buy is your first home. 

  - No SDLT up to £425,000
  - 5% SDLT on the portion from £425,001 to £625,000
  - If the price is over £625,000, you cannot claim the relief. 
  Source: https://www.gov.uk/stamp-duty-land-tax/residential-property-rates
  */

  const no_sdlt_threshold = 425_000;
  const no_tax_relief_claim_threshold = 625_000;

  if (price <= no_sdlt_threshold) {
    return 0;
  }

  // Over £625,000 - follow the rules for people who’ve bought a home before.
  if (price > no_tax_relief_claim_threshold) {
    return calculateSecondTimeBuyerCurrentSDLT(price);
  }

  // the price is between £425,001 to £625,000, so the 5% tax is applied
  return (price - no_sdlt_threshold) * 0.05;
};

const calculateSecondTimeBuyerCurrentSDLT = (price: number) => {
  /*
  Calculates the tax relief if you are not a first time buyer and this property 
  will be the only property you own.

  - No SDLT up to £250,000
  - 5% for the portion from £250,001 to £925,000
  - 10% for the portion from £925,001 to £1.5 million
  - 12% for the portion above £1.5 million

  Source: https://www.gov.uk/stamp-duty-land-tax/residential-property-rates
  */

  const no_sdlt_threshold = 250_000;
  const five_percent_treshold = 925_000;
  const ten_percent_threshold = 1_500_000;

  if (price <= no_sdlt_threshold) {
    return 0;
  }
  if (price <= five_percent_treshold) {
    return (price - no_sdlt_threshold) * 0.05;
  }
  if (price <= ten_percent_threshold) {
    return (
      (five_percent_treshold - no_sdlt_threshold) * 0.05 +
      (price - five_percent_treshold) * 0.1
    );
  }

  // above £1.5 million
  return (
    (five_percent_treshold - no_sdlt_threshold) * 0.05 +
    (ten_percent_threshold - five_percent_treshold) * 0.1 +
    (price - ten_percent_threshold) * 0.12
  );
};
